// Native platform detection + lightweight bridge.
//
// The React app runs in three modes:
//   1. Regular browser (Chrome/Safari/Edge)  → `isNative` = false
//   2. Installed PWA                          → `isNative` = false (still browser)
//   3. Native shell (iOS/Android Capacitor)   → `isNative` = true
//
// We lazy-load @capacitor/core so the browser build doesn't ship the SDK.

let _cached = null;

export function isNative() {
  if (_cached !== null) return _cached;
  try {
    _cached = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch {
    _cached = false;
  }
  return _cached;
}

export function nativePlatform() {
  try {
    return window.Capacitor?.getPlatform?.() || "web";
  } catch {
    return "web";
  }
}

/**
 * Register with the native push service (APNs / FCM) and forward the token
 * to /api/push/subscribe-native. Safe to call in browser — becomes a no-op.
 */
export async function registerNativePush(apiClient) {
  if (!isNative()) return { ok: false, reason: "not-native" };
  try {
    // Dynamic imports so the browser bundle doesn't include Capacitor code.
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return { ok: false, reason: "denied" };
    await PushNotifications.register();
    return new Promise((resolve) => {
      PushNotifications.addListener("registration", async ({ value: token }) => {
        try {
          await apiClient.post("/push/subscribe-native", {
            token,
            platform: nativePlatform(),
          });
          resolve({ ok: true, token });
        } catch (e) {
          resolve({ ok: false, reason: "server-error", error: e?.message });
        }
      });
      PushNotifications.addListener("registrationError", (err) => {
        resolve({ ok: false, reason: "registration-error", error: err?.error });
      });
    });
  } catch (e) {
    return { ok: false, reason: "sdk-missing", error: e?.message };
  }
}

/**
 * One-tap biometric unlock. Returns true if the user authenticated (or the
 * app is running in a browser and biometrics is not available — we still
 * consider "you're on your own laptop" as trusted).
 */
export async function biometricUnlock(reason = "Unlock SentryWatch") {
  if (!isNative()) return true;
  try {
    const { NativeBiometric, BiometryType } = await import("@capgo/capacitor-native-biometric");
    const available = await NativeBiometric.isAvailable();
    if (!available.isAvailable) return true; // no biometric hardware — allow
    await NativeBiometric.verifyIdentity({
      reason,
      title: "SentryWatch",
      subtitle: reason,
      description: BiometryType.FACE_AUTHENTICATION
        ? "Use Face ID to sign in"
        : "Use your fingerprint to sign in",
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Trigger a haptic buzz on native (button confirms, incident opened, etc).
 * Silently no-ops in the browser.
 */
export async function haptic(kind = "light") {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
    const style = kind === "heavy" ? ImpactStyle.Heavy
                : kind === "medium" ? ImpactStyle.Medium : ImpactStyle.Light;
    await Haptics.impact({ style });
  } catch { /* silent */ }
}

/**
 * Consume app-open deep links like `sentryit://verify/<token>` or
 * `https://watch.sentryit.com/auth/verify/<token>`. Call once on app boot.
 */
export function listenForDeepLinks(onUrl) {
  if (!isNative()) return () => {};
  let dispose = null;
  (async () => {
    try {
      const { App } = await import("@capacitor/app");
      const handle = await App.addListener("appUrlOpen", (event) => {
        try {
          const url = event?.url || "";
          if (url) onUrl(url);
        } catch { /* silent */ }
      });
      dispose = handle;
    } catch { /* SDK not present */ }
  })();
  return () => { try { dispose?.remove?.(); } catch { /* silent */ } };
}
